import React from 'react'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useComplianceFrameworks } from 'src/components/async/complianceframework'
import { useComplianceFrameworkObjectives } from 'src/components/async/Framework/useComplianceFrameworkObjectives'
import { FRAMEWORK_MODEL_IDS } from 'src/Utils/globalEnums'
import { FilterName, FilterParam } from '../FilterBar.types'
import { NONE_VALUE } from '../FilterBar.constants'
import { FilterDropdown } from './FilterDropdown'
import { NestedFilterType } from './FilterDropdown.constants'
import { LoadingFilterDropdown } from './FilterDropdown.components'

const CfModelIdToFilterParam: Record<FRAMEWORK_MODEL_IDS, FilterParam> = {
  [FRAMEWORK_MODEL_IDS.SOC2]: FilterParam.SOC2,
  [FRAMEWORK_MODEL_IDS.SOC1]: FilterParam.SOC1,
  [FRAMEWORK_MODEL_IDS.ISO27001]: FilterParam.ISO,
  [FRAMEWORK_MODEL_IDS.ISO27701_DC]: FilterParam.ISO27701_DC,
  [FRAMEWORK_MODEL_IDS.ISO27701_DP]: FilterParam.ISO27701_DP,
  [FRAMEWORK_MODEL_IDS.HIPAA]: FilterParam.HIPAA,
  [FRAMEWORK_MODEL_IDS.HITRUST]: FilterParam.HITRUST,
  [FRAMEWORK_MODEL_IDS.NISTCSF]: FilterParam.NISTCSF,
  [FRAMEWORK_MODEL_IDS.NYDFS]: FilterParam.NYDFS,
  [FRAMEWORK_MODEL_IDS.PCISAQD]: FilterParam.PCISAQD,
  [FRAMEWORK_MODEL_IDS.PCISAQA]: FilterParam.PCISAQA,
  [FRAMEWORK_MODEL_IDS.PIPEDA_DC]: FilterParam.PIPEDA_DC,
  [FRAMEWORK_MODEL_IDS.PIPEDA_DP]: FilterParam.PIPEDA_DP,
  [FRAMEWORK_MODEL_IDS.TEST]: FilterParam.TEST,
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A1]: FilterParam.SWIFT_CSCF_A1,
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A2]: FilterParam.SWIFT_CSCF_A2,
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A3]: FilterParam.SWIFT_CSCF_A3,
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A4]: FilterParam.SWIFT_CSCF_A4,
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_B]: FilterParam.SWIFT_CSCF_B,
  [FRAMEWORK_MODEL_IDS.SWIFT_PSCF]: FilterParam.SWIFT_PSCF,
  [FRAMEWORK_MODEL_IDS.TISAX_VDA_ISA]: FilterParam.TISAX_VDA_ISA,
  [FRAMEWORK_MODEL_IDS.LADMF]: FilterParam.LADMF,
  [FRAMEWORK_MODEL_IDS.NIST800171]: FilterParam.NIST800171,
  [FRAMEWORK_MODEL_IDS.GDPR_DC]: FilterParam.GDPR_DC,
  [FRAMEWORK_MODEL_IDS.GDPR_DP]: FilterParam.GDPR_DP,
  [FRAMEWORK_MODEL_IDS.CCPA]: FilterParam.CCPA,
  [FRAMEWORK_MODEL_IDS.PCIDSS]: FilterParam.PCIDSS,
  [FRAMEWORK_MODEL_IDS.ISO27701]: FilterParam.ISO27701,
  [FRAMEWORK_MODEL_IDS.GDPR]: FilterParam.GDPR,
  [FRAMEWORK_MODEL_IDS.DODCMMC]: FilterParam.DODCMMC,
  [FRAMEWORK_MODEL_IDS.CUSTOM]: FilterParam.CUSTOM_FRAMEWORK,
  [FRAMEWORK_MODEL_IDS.CMMC_LEVEL_1]: FilterParam.CMMC_LEVEL_1,
  [FRAMEWORK_MODEL_IDS.CMMC_LEVEL_2]: FilterParam.CMMC_LEVEL_2,
  [FRAMEWORK_MODEL_IDS.ISO27018]: FilterParam.ISO27018,
}

export const ComplianceFrameworkFilterDropdown = React.memo(
  ({ isNested = false }: { isNested?: boolean }): JSX.Element | null => {
    const {
      data: cfData,
      isLoading: cfLoading,
      error: cfError,
    } = useComplianceFrameworks(true)

    const complianceFrameworks = cfData?.getItemsList() || []
    const cfIds = complianceFrameworks?.map((cf) => cf.getId())
    const {
      data: requirementsData,
      isLoading: requirementsLoading,
      error: requirementsError,
    } = useComplianceFrameworkObjectives(cfIds)
    if (cfError || requirementsError) return <></>
    if (cfLoading || requirementsLoading) {
      return (
        <LoadingFilterDropdown
          filterName={FilterName.SOURCE}
          isNested={isNested}
        />
      )
    }

    const cfFilterNameToNestedFilter: Record<string, NestedFilterType> = {}
    complianceFrameworks.forEach((cf) => {
      const requirements =
        requirementsData
          ?.getItemsList()
          .filter(
            (objective) => objective.getComplianceFrameworkId() === cf.getId(),
          ) || []
      if (requirements && requirements.length > 0) {
        const nestedFilter: NestedFilterType = {
          filterParam:
            CfModelIdToFilterParam[cf.getModelId() as FRAMEWORK_MODEL_IDS],
          filterValues: [
            NONE_VALUE,
            ...requirements.map((requirement) => ({
              value: requirement.getModelId(),
              label: requirement.getName(),
            })),
          ],
        }
        cfFilterNameToNestedFilter[cf.getName()] = nestedFilter
      }
    })

    const filterValues = [
      NONE_VALUE,
      ...complianceFrameworks.map((cf: ComplianceFramework) => {
        return {
          value: cf.getModelId(),
          label: cf.getName(),
          nestedFilter: cfFilterNameToNestedFilter[cf.getName()],
        }
      }),
    ]

    return complianceFrameworks.length > 0 ? (
      <FilterDropdown
        filterParam={FilterParam.COMPLIANCE_FRAMEWORK}
        filterName={FilterName.COMPLIANCE_FRAMEWORK}
        filterValues={filterValues}
        hasNested
        includeParentFilter
        isNested={isNested}
      />
    ) : null
  },
)
